/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import * as Accordion from '@radix-ui/react-accordion'
import React, { useState } from 'react'
import { MdExpandMore } from 'react-icons/md'

import { ExpandableContentHeadingTag } from '@/components/ui/ExpandableContent'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type ManualExpandableContentCardProps = {
  /** The clickable title used to expand the content box. */
  title: string

  /** The heading level to render the title as. Defaults to `h2`. */
  headingTag?: ExpandableContentHeadingTag

  /** The content to show when the component is expanded. */
  content: React.ReactNode

  /** Whether the content should be expanded by default. */
  defaultOpen?: boolean

  /** An optional HTML id to use on the container element. */
  containerId?: string

  /** An optional icon to display next to the title. */
  icon?: React.ReactNode
}

/**
 * An accordion component that displays a title and content that can be expanded and collapsed. The title is always
 * visible, and the content is hidden until the user clicks on the title to expand it. An optional icon can be displayed
 * next to the title.
 */
const ManualExpandableContentCard: React.FC<ManualExpandableContentCardProps> = ({
  title,
  headingTag: HeadingTag = 'h2',
  content,
  defaultOpen = false,
  icon,
  containerId,
}) => {
  const [value, setValue] = useState(defaultOpen ? 'accordion-item' : undefined)

  return (
    <Accordion.Root
      id={containerId}
      collapsible
      type={'single'}
      className={cn(
        'flex flex-col gap-6 border-grey-50/20 bg-grey-450 px-10 py-6 my-2  bg-manuals-card-bg text-manuals-text text-base p-4 rounded-xl shadow-sm border mb-6',
      )}
      value={value}
      onValueChange={(newValue) => setValue(newValue)}
      asChild
    >
      <Accordion.Item value={'accordion-item'}>
        <Accordion.Trigger asChild>
          <div className={cn('group flex flex-row items-center justify-between cursor-pointer')}>
            <div className={'flex flex-row'}>
              {icon}
              <HeadingTag className={cn('p-0 m-0 font-semibold')}>{title}</HeadingTag>
            </div>
            <MdExpandMore className={'group-data-[state=open]:rotate-180'} />
          </div>
        </Accordion.Trigger>
        <Accordion.Content
          className={cn(
            'data-[state=open]:animate-slide-down data-[state=closed]:animate-slide-up overflow-hidden text-base ',
            'whitespace-pre-line',
          )}
        >
          {content}
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  )
}

export { ManualExpandableContentCard }
